import type { AbSegment, PageType } from '@/types/nav-types'

import { pageExperimentAssignments, type ExperimentAssignments } from './experiment-tracking'
import { AB_EXPERIMENTS } from './experiments'

const STORAGE_KEY = 'ht_ab_exposures:v1'
export const EXPERIMENT_ATTRIBUTION_MS = 30 * 24 * 60 * 60 * 1000

interface Exposure {
  variant: AbSegment
  at: number
}

interface ExposureStore {
  visitorId: string
  exposures: Record<string, Exposure>
}

let fallback: ExposureStore = { visitorId: '', exposures: {} }
let storageWriteFailed = false

const readExposures = (visitorId: string): Record<string, Exposure> => {
  if (storageWriteFailed && fallback.visitorId === visitorId) {
    return fallback.exposures
  }
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    // Storage can be disabled; the in-memory copy still carries SPA exposures.
    return fallback.visitorId === visitorId ? fallback.exposures : {}
  }
  try {
    const stored = JSON.parse(raw ?? 'null') as ExposureStore | null
    if (stored?.visitorId === visitorId && stored.exposures && typeof stored.exposures === 'object') {
      return stored.exposures
    }
    return {}
  } catch {
    return {}
  }
}

const currentExposures = (visitorId: string, now: number): Record<string, Exposure> => {
  const stored = readExposures(visitorId)
  return Object.fromEntries(
    AB_EXPERIMENTS.flatMap((experiment) => {
      const exposure = stored[experiment.trackingKey]
      if (
        !exposure ||
        (exposure.variant !== 'a' && exposure.variant !== 'b') ||
        !Number.isFinite(exposure.at) ||
        exposure.at < Date.parse(experiment.startedAt) ||
        ('endedAt' in experiment && exposure.at >= Date.parse(experiment.endedAt)) ||
        exposure.at > now ||
        now - exposure.at > EXPERIMENT_ATTRIBUTION_MS
      ) {
        return []
      }
      return [[experiment.trackingKey, exposure]]
    })
  )
}

const assignmentsFromExposures = (exposures: Record<string, Exposure>): ExperimentAssignments =>
  Object.fromEntries(Object.entries(exposures).map(([key, exposure]) => [key, exposure.variant]))

// Client link builders can reuse recorded exposures without creating one.
export const readExperimentAssignments = (visitorId: string, now = Date.now()): ExperimentAssignments =>
  assignmentsFromExposures(currentExposures(visitorId, now))

// First real page exposure fixes the arm. Carry it across routes, tabs and
// later visits for 30 days; a cookie identity change discards the old history.
export const recordExperimentExposure = ({
  visitorId,
  pathname,
  pageType,
  segment,
  now = Date.now(),
  components,
}: {
  visitorId: string
  pathname: string
  pageType: PageType
  segment: string
  now?: number
  components?: readonly string[]
}): ExperimentAssignments => {
  const exposures = currentExposures(visitorId, now)
  const assignments = pageExperimentAssignments({ pathname, pageType, segment, at: now, components })
  for (const [key, variant] of Object.entries(assignments)) {
    exposures[key] ??= { variant, at: now }
  }
  fallback = { visitorId, exposures }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback))
    storageWriteFailed = false
  } catch {
    storageWriteFailed = true
    // Attribution must never prevent navigation when storage is unavailable.
  }
  return assignmentsFromExposures(exposures)
}
